import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Headphones,
  Download,
} from "lucide-react";

const tracks = [
  {
    id: "prefazione",
    n: "01",
    title: "Prefazione — Il motto",
    subtitle: "Apertura · Foreword",
    src: "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/noco29uu_03_Prefazione.mp3",
  },
  {
    id: "capitolo-1",
    n: "02",
    title: "Capitolo 1 — La mia storia",
    subtitle: "Il sogno della libertà, l'Amerigo Vespucci",
    src: "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/d9uvecfm_05_CAPITOLO%201.mp3",
  },
];

function fmt(s) {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function Audiobook() {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [muted, setMuted] = useState(false);
  const [durations, setDurations] = useState({});

  const track = tracks[current];

  // Pre-load durations (using temporary Audio elements)
  useEffect(() => {
    tracks.forEach((t) => {
      const a = new Audio();
      a.preload = "metadata";
      a.src = t.src;
      a.addEventListener("loadedmetadata", () => {
        setDurations((d) => ({ ...d, [t.id]: a.duration }));
      });
    });
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;
    const a = audioRef.current;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => {
      if (current < tracks.length - 1) {
        setCurrent(current + 1);
      } else {
        setPlaying(false);
        setTime(0);
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [current]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.muted = muted;
  }, [muted]);

  // Auto-play on track change if was playing
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.load();
    if (playing) {
      const p = audioRef.current.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
    setTime(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      const p = audioRef.current.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      setPlaying(true);
    }
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    audioRef.current.currentTime = ratio * duration;
    setTime(ratio * duration);
  };

  const goPrev = () => setCurrent((c) => Math.max(0, c - 1));
  const goNext = () =>
    setCurrent((c) => Math.min(tracks.length - 1, c + 1));

  const progress = duration ? (time / duration) * 100 : 0;

  return (
    <section
      id="audiobook"
      data-testid="audiobook-section"
      className="relative py-24 md:py-32 border-t border-[#E5E1DA] bg-gradient-to-br from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6] overflow-hidden"
    >
      {/* Decorative orbs */}
      <div
        aria-hidden
        className="absolute -top-32 right-0 w-[45%] h-[70%] opacity-25 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #E89B6C 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-32 w-[40%] h-[60%] opacity-20 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #6EC1D9 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-10 mb-12 md:mb-16 items-end">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#E89B6C]/40 bg-[#E89B6C]/10 mb-5">
              <Headphones size={13} className="text-[#E89B6C]" strokeWidth={1.5} />
              <span className="overline text-[#E89B6C]">Audiolibro · Anteprima</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Ascolta una parte
              <br />
              <em className="text-[#E89B6C]">dell'audiolibro.</em>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-lg font-light text-[#F9F8F6]/80 leading-relaxed">
              Due tracce dall'audiolibro narrato dall'autore: la Prefazione
              "Il motto" e il Capitolo 1 "La mia storia". Per ascoltarlo
              dovunque sei.
            </p>
          </div>
        </div>

        {/* Player */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Now playing card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-7 bg-[#F9F8F6]/8 backdrop-blur-md border border-[#F9F8F6]/15 p-8 md:p-10"
            data-testid="audiobook-player"
          >
            <p className="overline text-[#E89B6C]">In ascolto · Traccia {track.n}</p>
            <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
              {track.title}
            </h3>
            <p className="text-sm font-light text-[#F9F8F6]/60 mt-1">
              {track.subtitle}
            </p>

            {/* Audio element */}
            <audio
              ref={audioRef}
              src={track.src}
              preload="metadata"
              data-testid="audiobook-audio"
            />

            {/* Progress bar */}
            <div
              className="mt-8 h-1 bg-[#F9F8F6]/15 cursor-pointer relative group"
              onClick={seek}
              data-testid="audiobook-progress"
              role="slider"
              aria-valuenow={Math.round(progress)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="absolute top-0 left-0 h-full bg-[#E89B6C] transition-all"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-[#E89B6C] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${progress}% - 6px)` }}
              />
            </div>

            {/* Time + controls */}
            <div className="mt-3 flex items-center justify-between font-sans-body text-xs tracking-widest text-[#F9F8F6]/70">
              <span data-testid="audiobook-time">{fmt(time)}</span>
              <span data-testid="audiobook-duration">{fmt(duration)}</span>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={() => setMuted(!muted)}
                aria-label={muted ? "Riattiva audio" : "Disattiva audio"}
                data-testid="audiobook-mute"
                className="w-10 h-10 flex items-center justify-center border border-[#F9F8F6]/20 hover:border-[#F9F8F6]/60 transition-colors"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={goPrev}
                  disabled={current === 0}
                  aria-label="Traccia precedente"
                  data-testid="audiobook-prev"
                  className="w-11 h-11 flex items-center justify-center border border-[#F9F8F6]/20 hover:border-[#F9F8F6]/60 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <SkipBack size={16} />
                </button>
                <button
                  onClick={toggle}
                  aria-label={playing ? "Pausa" : "Play"}
                  data-testid="audiobook-play-toggle"
                  className="w-16 h-16 flex items-center justify-center bg-[#E89B6C] text-[#0B192C] hover:bg-[#F9F8F6] transition-colors"
                >
                  {playing ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />}
                </button>
                <button
                  onClick={goNext}
                  disabled={current === tracks.length - 1}
                  aria-label="Traccia successiva"
                  data-testid="audiobook-next"
                  className="w-11 h-11 flex items-center justify-center border border-[#F9F8F6]/20 hover:border-[#F9F8F6]/60 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <SkipForward size={16} />
                </button>
              </div>

              <a
                href={track.src}
                download
                aria-label="Scarica traccia"
                data-testid="audiobook-download"
                className="w-10 h-10 flex items-center justify-center border border-[#F9F8F6]/20 hover:border-[#F9F8F6]/60 transition-colors"
              >
                <Download size={16} />
              </a>
            </div>
          </motion.div>

          {/* Track list */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="md:col-span-5"
          >
            <p className="overline text-[#E89B6C] mb-4">Tracce disponibili</p>
            <div className="space-y-2">
              {tracks.map((t, i) => {
                const active = i === current;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setCurrent(i);
                      setPlaying(true);
                    }}
                    data-testid={`audiobook-track-${t.id}`}
                    className={`w-full text-left flex items-center gap-4 p-4 border transition-all duration-300 ${
                      active
                        ? "bg-[#E89B6C]/15 border-[#E89B6C]"
                        : "bg-[#F9F8F6]/5 border-[#F9F8F6]/10 hover:border-[#F9F8F6]/30"
                    }`}
                  >
                    <span
                      className={`w-10 h-10 flex-shrink-0 flex items-center justify-center font-serif-display text-lg ${
                        active
                          ? "bg-[#E89B6C] text-[#0B192C]"
                          : "border border-[#F9F8F6]/20 text-[#F9F8F6]/70"
                      }`}
                    >
                      {active && playing ? <Pause size={14} fill="currentColor" /> : t.n}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`font-serif-display text-base md:text-lg leading-tight truncate ${
                          active ? "text-[#F9F8F6]" : "text-[#F9F8F6]/85"
                        }`}
                      >
                        {t.title}
                      </p>
                      <p className="text-xs text-[#F9F8F6]/50 mt-0.5 truncate">
                        {t.subtitle}
                      </p>
                    </div>
                    <span className="text-xs text-[#F9F8F6]/50 font-sans-body tracking-widest">
                      {fmt(durations[t.id] || 0)}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="mt-6 text-xs uppercase tracking-[0.18em] text-[#F9F8F6]/50">
              Anteprima audiolibro · Versione completa in arrivo
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
